import type { SupportedSession, TellescopeTanstackDefaultsConfig } from "../types";
import { TellescopeSessionContext, TellescopeTanstackConfigContext } from "./context";
import {
  TellescopeDataSyncPollingProvider,
  TellescopeDataSyncPollingProviderArgs,
} from "./TellescopeDataSyncPollingWrapper";
import { ReactNode, useEffect } from "react";

/**
 * Props for the TellescopeQueryProvider component.
 *
 * @property session - The Tellescope SDK session instance to provide to child components.
 *   Accepts both `Session` (staff) and `EnduserSession` (patient) types.
 * @property children - React child elements that will have access to the session context
 * @property dataSyncPollingConfig (optional) - The options that defined the behavior of the data sync polling mechanism
 */
export interface TellescopeQueryProviderProps {
  children: ReactNode;
  dataSyncPollingConfig?: Partial<Omit<TellescopeDataSyncPollingProviderArgs, "children">>;
  session: SupportedSession | null;
  requestNotificationPermissionOnMount?: boolean;
  defaults?: TellescopeTanstackDefaultsConfig;
}

/**
 * Provider component that makes a Tellescope SDK session available to child components.
 *
 * Wrap your application or a section of your component tree with this provider to enable
 * the use of Tellescope query hooks without manually passing the session to each hook.
 *
 * @param session (optional) - Tellescope session
 * @param dataSyncPollingConfig (optional) - Options for data-sync endpoint polling
 * @returns The provider component wrapping the children
 *
 * @example
 * ```tsx
 * <TellescopeQueryProvider session={tellescopeSession}>
 *   <App />
 * </TellescopeQueryProvider>
 * ```
 */
export const TellescopeQueryProvider = ({
  session,
  children,
  dataSyncPollingConfig,
  requestNotificationPermissionOnMount,
  defaults,
}: TellescopeQueryProviderProps) => {
  const { pollingRateInMs = 5000, ...dataSyncRest } = dataSyncPollingConfig ?? {};

  useEffect(() => {
    if (
      requestNotificationPermissionOnMount &&
      typeof Notification !== "undefined" &&
      Notification.permission !== "denied" &&
      Notification.permission !== "granted" &&
      !!session?.authToken
    ) {
      Notification.requestPermission();
    }
  }, [requestNotificationPermissionOnMount, session?.authToken]);

  return (
    <TellescopeSessionContext.Provider value={session}>
      <TellescopeTanstackConfigContext.Provider value={defaults}>
        <TellescopeDataSyncPollingProvider pollingRateInMs={pollingRateInMs} {...dataSyncRest}>
          {children}
        </TellescopeDataSyncPollingProvider>
      </TellescopeTanstackConfigContext.Provider>
    </TellescopeSessionContext.Provider>
  );
};
